import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth';
import { PageLayout } from '@/components/layout';
import { useMessagingFlow } from '@/stackflow';
import { ChatAvatar, PinnedMessagesPanel, StarredMessagesPanel } from '../components';
import {
  useConversation,
  useUpdateConversation,
  useAddParticipants,
  useRemoveParticipant,
  useLeaveConversation,
  useMuteConversation,
  useJoinChannel,
  useUpdateParticipantRole,
} from '../hooks';
import { useBatchPresence } from '../hooks/usePresence';
import http from '@/network/http';

interface UserResult {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatarUrl?: string | null;
}

interface Props {
  conversationId: string;
}

export default function ChannelInfoPage({ conversationId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pop } = useMessagingFlow();
  const currentUserId = useAuthStore((s) => s.user?.id) || '';

  // Data
  const { data: conversation, isLoading } = useConversation(conversationId);
  const userIds = useMemo(
    () => conversation?.participants.map((p) => p.userId).filter((id) => id !== currentUserId) || [],
    [conversation, currentUserId],
  );
  const { presenceMap } = useBatchPresence(userIds);

  // Mutations
  const updateConversation = useUpdateConversation();
  const addParticipants = useAddParticipants();
  const removeParticipant = useRemoveParticipant();
  const leaveConversation = useLeaveConversation();
  const muteConversation = useMuteConversation();
  const joinChannel = useJoinChannel();
  const updateParticipantRole = useUpdateParticipantRole();

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Add members state
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  // Member action menu
  const [actionMenuUserId, setActionMenuUserId] = useState<string | null>(null);

  // Confirm dialogs
  const [confirmRemove, setConfirmRemove] = useState<{ userId: string; name: string } | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const isChannel = conversation?.type === 'channel';
  const myRole = conversation?.myRole;
  const isAdmin = myRole === 'admin';
  const isMuted = conversation?.isMuted;

  const infoTitle = isChannel
    ? t('messaging.channelInfo.title')
    : t('messaging.channelInfo.groupInfo');

  // ── Editing ──

  const startEditing = useCallback(() => {
    if (!conversation) return;
    setEditName(conversation.name || '');
    setEditDescription(conversation.description || '');
    setIsEditing(true);
  }, [conversation]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const saveEditing = useCallback(async () => {
    if (!conversation) return;
    await updateConversation.mutateAsync({
      id: conversation.id,
      name: editName.trim(),
      description: editDescription.trim(),
    });
    setIsEditing(false);
  }, [conversation, editName, editDescription, updateConversation]);

  // ── Add Members query ──

  const { data: addMembersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['messaging', 'users', searchQuery],
    queryFn: () =>
      http
        .get<{ users: UserResult[] }>('/messaging/users', {
          params: searchQuery.length >= 2 ? { search: searchQuery } : undefined,
        })
        .then((r) => r.data),
    enabled: showAddMembers,
    staleTime: 30_000,
  });

  const candidateUsers = useMemo(() => {
    const existingIds = new Set(conversation?.participants.map((p) => p.userId) || []);
    return (addMembersData?.users || []).filter(
      (u) => u.id !== currentUserId && !existingIds.has(u.id),
    );
  }, [addMembersData, conversation, currentUserId]);

  const candidateIds = useMemo(() => candidateUsers.map((u) => u.id), [candidateUsers]);
  const { presenceMap: candidatePresence } = useBatchPresence(showAddMembers ? candidateIds : []);

  const sortedCandidates = useMemo(
    () =>
      [...candidateUsers].sort((a, b) => {
        const aOn = candidatePresence[a.id]?.isOnline ? 1 : 0;
        const bOn = candidatePresence[b.id]?.isOnline ? 1 : 0;
        return bOn - aOn;
      }),
    [candidateUsers, candidatePresence],
  );

  const handleAddUser = useCallback(async (userId: string) => {
    await addParticipants.mutateAsync({ conversationId, participantIds: [userId] });
    queryClient.invalidateQueries({ queryKey: ['messaging', 'users'] });
  }, [conversationId, addParticipants, queryClient]);

  // ── Member actions ──

  const handleMakeAdmin = useCallback(async (userId: string) => {
    await updateParticipantRole.mutateAsync({ conversationId, userId, role: 'admin' });
    setActionMenuUserId(null);
  }, [conversationId, updateParticipantRole]);

  const handleRemoveAdmin = useCallback(async (userId: string) => {
    await updateParticipantRole.mutateAsync({ conversationId, userId, role: 'member' });
    setActionMenuUserId(null);
  }, [conversationId, updateParticipantRole]);

  const handleRemoveMember = useCallback(async (userId: string) => {
    await removeParticipant.mutateAsync({ conversationId, userId });
    setConfirmRemove(null);
    setActionMenuUserId(null);
  }, [conversationId, removeParticipant]);

  // ── Mute / Leave / Join ──

  const handleToggleMute = useCallback(() => {
    muteConversation.mutate({ conversationId, isMuted: !isMuted });
  }, [conversationId, isMuted, muteConversation]);

  const handleLeave = useCallback(async () => {
    await leaveConversation.mutateAsync(conversationId);
    setConfirmLeave(false);
    navigate('/messages', { replace: true });
  }, [conversationId, leaveConversation, navigate]);

  const handleJoin = useCallback(async () => {
    await joinChannel.mutateAsync(conversationId);
  }, [conversationId, joinChannel]);

  // ── Loading / Not found ──

  if (isLoading) {
    return (
      <PageLayout topBar={<div className="flex-1 text-center text-sm font-semibold text-neutral-600">{infoTitle}</div>}>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
        </div>
      </PageLayout>
    );
  }

  if (!conversation) {
    return (
      <PageLayout topBar={<div className="flex-1 text-center text-sm font-semibold text-neutral-600">{infoTitle}</div>}>
        <div className="flex h-64 items-center justify-center text-neutral-500">Not found</div>
      </PageLayout>
    );
  }

  const displayName = conversation.name || (isChannel ? 'Channel' : 'Group');
  const isJoined = !!conversation.myRole;
  const createdDate = new Date(conversation.createdAt).toLocaleDateString();

  // ── TopBar ──

  const topBar = (
    <>
      <button
        type="button"
        onClick={() => pop()}
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/80 transition-all active:scale-95"
      >
        <svg className="h-4 w-4 text-neutral-700 rtl:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <h1 className="flex-1 text-center text-base font-semibold text-neutral-800">{infoTitle}</h1>

      {isAdmin && !isEditing ? (
        <button
          type="button"
          onClick={startEditing}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/80 transition-all active:scale-95"
        >
          <svg className="h-4 w-4 text-neutral-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5z" />
          </svg>
        </button>
      ) : isEditing ? (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={cancelEditing}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-neutral-600 transition-all hover:bg-white/40"
          >
            {t('messaging.channelInfo.cancel')}
          </button>
          <button
            type="button"
            onClick={saveEditing}
            disabled={updateConversation.isPending}
            className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-medium text-white transition-all active:scale-95 disabled:opacity-50"
          >
            {t('messaging.channelInfo.save')}
          </button>
        </div>
      ) : (
        <div className="w-9" />
      )}
    </>
  );

  return (
    <PageLayout topBar={topBar}>
      <div className="space-y-5 p-4">
        {/* ── Channel Header ── */}
        <div className="flex flex-col items-center gap-3 pt-2">
          <ChatAvatar
            src={conversation.avatar}
            name={displayName}
            size="h-20 w-20"
            borderColor="border-slate-300"
          />

          {isEditing ? (
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full max-w-xs rounded-xl border border-white/30 bg-white/60 px-4 py-2.5 text-center text-lg font-semibold text-neutral-800 focus:border-slate-400 focus:outline-none"
              maxLength={50}
            />
          ) : (
            <h2 className="text-lg font-bold text-neutral-800">{displayName}</h2>
          )}

          {isEditing ? (
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              rows={2}
              className="w-full max-w-xs rounded-xl border border-white/30 bg-white/60 px-4 py-2 text-center text-sm text-neutral-600 focus:border-slate-400 focus:outline-none resize-none"
              maxLength={200}
              placeholder={t('messaging.channelInfo.editChannel')}
            />
          ) : conversation.description ? (
            <p className="max-w-xs text-center text-sm text-neutral-500">{conversation.description}</p>
          ) : null}

          {/* Category + public/private badges */}
          <div className="flex items-center gap-2">
            {conversation.category && (
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                {conversation.category}
              </span>
            )}
            {isChannel && (
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                conversation.isPublic
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700'
              }`}>
                {conversation.isPublic
                  ? t('messaging.channelInfo.publicChannel')
                  : t('messaging.channelInfo.privateChannel')}
              </span>
            )}
          </div>

          <p className="text-xs text-neutral-400">
            {t('messaging.members', { count: conversation.participants.length })}
            {' \u00b7 '}
            {t('messaging.channelInfo.createdOn', { date: createdDate })}
          </p>
        </div>

        {/* ── Action Buttons ── */}
        {isJoined ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleToggleMute}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/60 px-4 py-3 text-sm font-medium text-neutral-700 transition-all active:scale-[0.98]"
            >
              {isMuted ? (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 5L6 9H2v6h4l5 4V5z" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                  {t('messaging.channelInfo.unmuteChannel')}
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 010 7.07" />
                  </svg>
                  {t('messaging.channelInfo.muteChannel')}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => setConfirmLeave(true)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm font-medium text-error-700 transition-all active:scale-[0.98]"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {isChannel ? t('messaging.channelInfo.leaveChannel') : t('messaging.channelInfo.leaveGroup')}
            </button>
          </div>
        ) : isChannel && conversation.isPublic ? (
          <button
            type="button"
            onClick={handleJoin}
            disabled={joinChannel.isPending}
            className="w-full rounded-xl bg-slate-700 px-4 py-3 text-sm font-medium text-white transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {joinChannel.isPending
              ? t('messaging.channelInfo.joining')
              : t('messaging.channelInfo.joinChannel')}
          </button>
        ) : null}

        {/* ── Pinned Messages ── */}
        <PinnedMessagesPanel conversationId={conversationId} />

        {/* ── Starred Messages ── */}
        <StarredMessagesPanel
          conversationId={conversationId}
          onMessageTap={(messageId) => {
            navigate(`/messages/${conversationId}?scrollTo=${messageId}`);
          }}
        />

        {/* ── Members Section ── */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-neutral-700">
              {t('messaging.channelInfo.membersSection')} ({conversation.participants.length})
            </h3>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowAddMembers(!showAddMembers)}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition-all active:scale-95"
              >
                {t('messaging.channelInfo.addMembers')}
              </button>
            )}
          </div>

          {/* Add Members Panel */}
          {showAddMembers && (
            <div className="space-y-2 rounded-xl border border-white/20 bg-white/50 p-3">
              {/* Search bar */}
              <div className="relative">
                <svg
                  className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('messaging.channelInfo.searchUsers')}
                  dir="auto"
                  className="w-full rounded-xl border border-white/20 glass-surface py-2.5 pe-4 ps-10 text-sm text-neutral-800 placeholder:text-neutral-400 transition-all focus:shadow-sm focus:outline-none"
                />
              </div>

              {/* User list */}
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {isLoadingUsers ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-[56px] animate-pulse rounded-xl border border-white/10 bg-white/30"
                    />
                  ))
                ) : sortedCandidates.length > 0 ? (
                  sortedCandidates.map((user) => {
                    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
                    const isOnline = candidatePresence[user.id]?.isOnline;

                    return (
                      <div
                        key={user.id}
                        className="flex items-center gap-3 rounded-xl border border-white/20 glass-surface px-3 py-2.5"
                      >
                        {/* Avatar + online dot */}
                        <div className="relative flex-shrink-0">
                          <ChatAvatar src={user.avatarUrl} name={fullName} size="h-10 w-10" />
                          {isOnline && (
                            <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                          )}
                        </div>

                        {/* User info */}
                        <div className="flex-1 overflow-hidden" dir="auto">
                          <div className="truncate text-sm font-medium text-neutral-800">{fullName}</div>
                          <div className="flex items-center gap-1.5">
                            <span className="rounded border-[1px] border-white/20 bg-white/30 px-1 py-0 text-[10px] font-medium text-neutral-500">
                              {t(`roles.${user.role.toLowerCase()}`, user.role)}
                            </span>
                            <span className="truncate text-xs text-neutral-500">{user.email}</span>
                          </div>
                        </div>

                        {/* Add button */}
                        <button
                          type="button"
                          onClick={() => handleAddUser(user.id)}
                          disabled={addParticipants.isPending}
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-white/20 glass-surface transition-all hover:bg-white/60 active:scale-95 disabled:opacity-50"
                        >
                          <svg className="h-4 w-4 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" />
                            <line x1="5" y1="12" x2="19" y2="12" />
                          </svg>
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/30">
                      <svg className="h-5 w-5 text-neutral-800/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    <p className="text-xs text-neutral-400">
                      {t('messaging.channelInfo.noUsersToAdd')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Members List */}
          <div className="space-y-0.5 rounded-xl border border-white/20 bg-white/50">
            {conversation.participants.map((p) => {
              const isMe = p.userId === currentUserId;
              const isOnline = presenceMap[p.userId]?.isOnline || false;
              const showMenu = actionMenuUserId === p.userId;

              return (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-white/40"
                >
                  {/* Avatar + online dot */}
                  <div className="relative flex-shrink-0">
                    <ChatAvatar src={p.user.avatar} name={p.user.name} size="h-10 w-10" />
                    {isOnline && (
                      <div className="absolute -bottom-0.5 -end-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                    )}
                  </div>

                  {/* Name + badges */}
                  <div className="flex-1 overflow-hidden">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium text-neutral-800">{p.user.name}</span>
                      {isMe && (
                        <span className="text-xs text-neutral-400">{t('messaging.channelInfo.you')}</span>
                      )}
                    </div>
                    {p.role === 'admin' && (
                      <span className="text-[11px] font-medium text-slate-600">
                        {t('messaging.channelInfo.admin')}
                      </span>
                    )}
                  </div>

                  {/* Admin actions menu */}
                  {isAdmin && !isMe && (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setActionMenuUserId(showMenu ? null : p.userId)}
                        className="flex h-8 w-8 items-center justify-center rounded-full transition-all hover:bg-white/60 active:scale-95"
                      >
                        <svg className="h-4 w-4 text-neutral-500" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="5" r="1.5" />
                          <circle cx="12" cy="12" r="1.5" />
                          <circle cx="12" cy="19" r="1.5" />
                        </svg>
                      </button>

                      {showMenu && (
                        <div className="absolute end-0 top-9 z-50 w-44 rounded-xl border border-white/30 bg-white/95 py-1 shadow-xl backdrop-blur-lg">
                          {p.role === 'member' ? (
                            <button
                              type="button"
                              onClick={() => handleMakeAdmin(p.userId)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 transition-all hover:bg-slate-50"
                            >
                              {t('messaging.channelInfo.makeAdmin')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRemoveAdmin(p.userId)}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-neutral-700 transition-all hover:bg-slate-50"
                            >
                              {t('messaging.channelInfo.removeAdmin')}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmRemove({ userId: p.userId, name: p.user.name });
                              setActionMenuUserId(null);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-error-600 transition-all hover:bg-error-50"
                          >
                            {t('messaging.channelInfo.removeMember')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Confirm Remove Member Dialog ── */}
      {confirmRemove && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-center text-sm font-medium text-neutral-800">
              {t('messaging.channelInfo.removeMemberConfirm', { name: confirmRemove.name })}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmRemove(null)}
                className="flex-1 rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-600 transition-all active:scale-95"
              >
                {t('messaging.channelInfo.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleRemoveMember(confirmRemove.userId)}
                disabled={removeParticipant.isPending}
                className="flex-1 rounded-xl bg-error-600 px-4 py-2.5 text-sm font-medium text-white transition-all active:scale-95 disabled:opacity-50"
              >
                {t('messaging.channelInfo.removeMember')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Leave Dialog ── */}
      {confirmLeave && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <p className="text-center text-sm font-medium text-neutral-800">
              {t('messaging.channelInfo.leaveConfirm')}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setConfirmLeave(false)}
                className="flex-1 rounded-xl border border-neutral-200 px-4 py-2.5 text-sm font-medium text-neutral-600 transition-all active:scale-95"
              >
                {t('messaging.channelInfo.cancel')}
              </button>
              <button
                type="button"
                onClick={handleLeave}
                disabled={leaveConversation.isPending}
                className="flex-1 rounded-xl bg-error-600 px-4 py-2.5 text-sm font-medium text-white transition-all active:scale-95 disabled:opacity-50"
              >
                {isChannel ? t('messaging.channelInfo.leaveChannel') : t('messaging.channelInfo.leaveGroup')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
